import type { Node, Edge } from "@xyflow/react";
import type {
    IvrApiAudio,
    IvrApiElement,
    IvrApiRoute,
    IvrApiSchedule,
    IvrSchedulerSlotRow,
} from "./ivrSchemeHydration";
import {
    IVR_BASE_NODE_ID,
    IVR_START_CONFIG_PREFIX,
    ivrRawScheduleElementId,
    ivrRawScheduleIsActive,
    ivrRawSchedulePriority,
    ivrRawScheduleRowId,
    resolveEffectiveIvrEntryElementId,
} from "./ivrSchemeHydration";
import { IVR_END_ELEMENT_RESERVED_ID } from "./ivrSchemeConstants";
import {
    buildIvrElementDraftDataString,
    filterIvrSchedulerSlotRowsForScheduleApi,
    normalizeIvrSchedulerScheduleKind,
} from "./ivrElementDraftData";

type BlockCfg = Record<string, unknown>;

type AudioMetaRow = {
    audioId: number;
    audioName: string;
    groupId?: number | null;
    groupName?: string | null;
};

const START_CONF_TO_TYPE: Record<string, string> = {
    End: "END",
    Play: "PLAY",
    Menu: "MENU",
    Scheduler: "SCHEDULER",
    AgentHunter: "OPERATOR",
};

function resolveElementType(cfg: BlockCfg): string {
    const raw = (cfg.elementType as string | undefined)?.trim();
    if (raw) {
        const u = raw.toUpperCase();
        if (u === "AGENTHUNTER" || u === "HUNTER" || u === "AGENT_HUNTER") {
            return "OPERATOR";
        }
        return u;
    }
    const sc = (cfg.startConfiguration as string) || "Play";
    return START_CONF_TO_TYPE[sc] ?? "PLAY";
}

function audiosFromMeta(meta: AudioMetaRow[] | undefined): IvrApiAudio[] | null {
    if (!meta?.length) return null;
    return meta.map((m) => ({
        audioId: m.audioId,
        audioName: m.audioName,
        groupId: m.groupId ?? null,
        ...(m.groupName != null ? { groupName: m.groupName } : {}),
    }));
}

function parseElementId(cfg: BlockCfg): number {
    const raw = cfg.elementId;
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && !Number.isNaN(n) ? n : 0;
}

export function blockConfigToApiElement(
    blockConfig: BlockCfg,
    flowId: number | undefined | null
): IvrApiElement {
    const sc = (blockConfig.startConfiguration as string) || "Play";
    const elementType = resolveElementType(blockConfig);
    let elementId = parseElementId(blockConfig);
    if ((sc === "End" || elementType === "END") && elementId <= 0) {
        elementId = IVR_END_ELEMENT_RESERVED_ID;
    }

    const title =
        (blockConfig.startConfigTitle as string | undefined)?.trim() ||
        (blockConfig.name as string | undefined) ||
        null;

    const base: IvrApiElement = {
        flowId: flowId ?? undefined,
        elementId,
        name: title,
        elementType,
        audios: null,
        elementData: (blockConfig.elementData as unknown) ?? null,
        minDigits: (blockConfig.minDigits as number | null) ?? null,
        maxDigits: (blockConfig.maxDigits as number | null) ?? null,
        terminators: blockConfig.terminators ?? null,
        regexp: (blockConfig.regexp as string | null) ?? null,
        digitTimeOut: (blockConfig.digitTimeOut as number | null) ?? null,
        timeOut: null,
        silencePre: null,
        silencePost: null,
    };

    if (sc === "End" || elementType === "END") {
        return {
            ...base,
            name: (blockConfig.name as string) ?? base.name ?? "END",
            audios: (blockConfig.audios as IvrApiElement["audios"]) ?? null,
            timeOut: (blockConfig.timeOut as number | null) ?? null,
            silencePre: (blockConfig.silencePre as number | null) ?? null,
            silencePost: (blockConfig.silencePost as number | null) ?? null,
        };
    }

    if (sc === "Play") {
        return {
            ...base,
            audios: audiosFromMeta(blockConfig.playAudioMeta as AudioMetaRow[] | undefined),
            timeOut: (blockConfig.playTimeout as number | null) ?? null,
            silencePre: (blockConfig.playSilensPre as number | null) ?? null,
            silencePost: (blockConfig.playSilensPost as number | null) ?? null,
        };
    }

    if (sc === "Menu") {
        const draftData = buildIvrElementDraftDataString(
            blockConfig,
            elementType
        );
        return {
            ...base,
            audios: audiosFromMeta(blockConfig.menuAudioMeta as AudioMetaRow[] | undefined),
            timeOut: (blockConfig.menuTimeout as number | null) ?? null,
            silencePre: (blockConfig.menuSilensPre as number | null) ?? null,
            silencePost: (blockConfig.menuSilensPost as number | null) ?? null,
            ...(draftData != null ? { draftData } : {}),
        };
    }

    if (sc === "Scheduler") {
        const draftData = buildIvrElementDraftDataString(
            blockConfig,
            elementType
        );
        return {
            ...base,
            audios: null,
            ...(draftData != null ? { draftData } : {}),
        };
    }

    if (sc === "AgentHunter") {
        const qids = (blockConfig.hunterQueueIds as unknown[] | undefined) ?? [];
        let queueId: number | null = null;
        for (const raw of qids) {
            const n = typeof raw === "number" ? raw : Number(raw);
            if (Number.isFinite(n) && n > 0) {
                queueId = n;
                break;
            }
        }
        const opName =
            (title != null && String(title).trim()) || "Operator";
        return {
            ...base,
            elementType: "OPERATOR",
            name: opName,
            elementData: queueId != null ? String(queueId) : null,
            audios: null,
        };
    }

    return base;
}

export function normalizeIvrScheduleApiTime(t: string | null | undefined): string {
    const trimmed = String(t ?? "").trim();
    const p = trimmed.split(":");
    const h = Math.min(23, Math.max(0, parseInt(p[0] || "0", 10) || 0));
    const m = Math.min(59, Math.max(0, parseInt(p[1] || "0", 10) || 0));
    const s = Math.min(59, Math.max(0, parseInt(p[2] || "0", 10) || 0));
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function slotRowsToSchedules(
    elementId: number,
    flowId: number | undefined | null,
    rows: IvrSchedulerSlotRow[]
): IvrApiSchedule[] {
    return rows.map((row) => {
        const ridRaw = row.id;
        const rid =
            ridRaw != null && typeof ridRaw === "number"
                ? ridRaw
                : Number(String(ridRaw ?? "").trim());
        return {
            ...(Number.isFinite(rid) && rid > 0 ? { id: rid } : {}),
            flowId: flowId ?? undefined,
            elementId,
            priority: row.priority,
            daysMask: row.daysMask,
            fromTime: normalizeIvrScheduleApiTime(row.start),
            toTime: normalizeIvrScheduleApiTime(row.end),
            activeToId: row.activeToId,
        };
    });
}

export function findInactiveSchedulerSlotMatchingPostCandidate(
    elementId: number,
    slot: Pick<
        IvrApiSchedule,
        "daysMask" | "fromTime" | "toTime" | "activeToId"
    > & { id?: number },
    sources: readonly (readonly IvrApiSchedule[] | undefined)[]
): IvrApiSchedule | null {
    const flat: IvrApiSchedule[] = [];
    for (const src of sources) {
        if (src?.length) flat.push(...src);
    }
    const candFrom = normalizeIvrScheduleApiTime(slot.fromTime);
    const candTo = normalizeIvrScheduleApiTime(slot.toTime);
    const candMask = Number(slot.daysMask) || 0;
    const candAid = Number(slot.activeToId) || 0;

    const preferId =
        slot.id != null
            ? typeof slot.id === "number"
                ? slot.id
                : Number(String(slot.id).trim())
            : NaN;
    if (Number.isFinite(preferId) && preferId > 0) {
        const hit = flat.find((x) => {
            if (ivrRawScheduleRowId(x) !== preferId) return false;
            if (ivrRawScheduleElementId(x) !== elementId) return false;
            return ivrRawScheduleIsActive(x) === false;
        });
        if (hit) return hit;
    }

    const match = flat.find((x) => {
        if (ivrRawScheduleElementId(x) !== elementId) return false;
        if (ivrRawScheduleIsActive(x) !== false) return false;
        const r = x as Record<string, unknown>;
        const m = Number(r.daysMask ?? r.DaysMask) || 0;
        if (m !== candMask) return false;
        const fromX = normalizeIvrScheduleApiTime(
            String(r.fromTime ?? r.FromTime ?? "")
        );
        const toX = normalizeIvrScheduleApiTime(
            String(r.toTime ?? r.ToTime ?? "")
        );
        if (fromX !== candFrom || toX !== candTo) return false;
        const aid = Number(r.activeToId ?? r.ActiveToId) || 0;
        return aid === candAid;
    });
    return match ?? null;
}

export function nextIvrRoutePriority(
    fromElementId: number,
    routes: IvrApiRoute[]
): number {
    let max = 0;
    for (const r of routes) {
        if (r.fromElementId !== fromElementId) continue;
        if (r.isActive === false) continue;
        const p = Number(r.priority);
        if (Number.isFinite(p) && p > max) max = p;
    }
    return max + 1;
}

export function nextIvrSchedulePostPriorityFromSources(
    scheduleElementId: number,
    ...sources: IvrApiSchedule[][]
): number {
    const el = Number(scheduleElementId);
    if (!Number.isFinite(el) || el <= 0) return 1;
    let max = 0;
    for (const schedules of sources) {
        if (!schedules?.length) continue;
        for (const s of schedules) {
            if (ivrRawScheduleElementId(s) !== el) continue;
            const p = ivrRawSchedulePriority(s);
            if (Number.isFinite(p) && p > max) max = p;
        }
    }
    return max + 1;
}

export function nextIvrSchedulePostPriority(
    scheduleElementId: number,
    schedules: IvrApiSchedule[]
): number {
    return nextIvrSchedulePostPriorityFromSources(scheduleElementId, schedules);
}

export type IvrNodeSerializeData = { blockConfig?: BlockCfg };

function collectRoutesFromEdges(
    nodes: Node<IvrNodeSerializeData>[],
    edges: Edge[]
): IvrApiRoute[] {
    const nodeIdToElementId = new Map<string, number>();
    for (const n of nodes) {
        if (!n.id.startsWith(IVR_START_CONFIG_PREFIX)) continue;
        const raw = n.data?.blockConfig?.elementId;
        if (raw == null || raw === "") continue;
        const num = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(num)) continue;
        nodeIdToElementId.set(n.id, num);
    }

    const out: IvrApiRoute[] = [];
    const seen = new Set<string>();

    for (const e of edges) {
        const pack = e.data as { ivrRoute?: IvrApiRoute } | undefined;
        const rt = pack?.ivrRoute;
        if (!rt) continue;

        const tgtNode = nodes.find((n) => n.id === e.target);
        if (!tgtNode?.id.startsWith(IVR_START_CONFIG_PREFIX)) continue;
        if (!nodeIdToElementId.has(tgtNode.id)) continue;
        const toEl = nodeIdToElementId.get(tgtNode.id)!;

        const next: IvrApiRoute = {
            ...rt,
            toElementId: toEl,
        };

        const dedupKey = `${next.id ?? "noid"}|${next.fromElementId}|${next.toElementId}|${next.routeType}|${next.routeValue ?? ""}|${next.priority ?? 0}`;
        if (seen.has(dedupKey)) continue;
        seen.add(dedupKey);

        out.push(next);
    }

    return out;
}

export type IvrSerializedSchemeSlice = {
    elements: IvrApiElement[];
    routes: IvrApiRoute[];
    schedules: IvrApiSchedule[];
    configurationPatch: { entryElementId: number | null };
};

export function serializeIvrGraphToApiParts(
    nodes: Node<IvrNodeSerializeData>[],
    edges: Edge[],
    configuration:
        | { flowId?: number | null; entryElementId?: number | null }
        | undefined
        | null
): IvrSerializedSchemeSlice {
    const flowId = configuration?.flowId ?? undefined;

    const configNodes = nodes
        .filter((n) => {
            if (!n.id.startsWith(IVR_START_CONFIG_PREFIX)) return false;
            const cfg = n.data?.blockConfig as BlockCfg | undefined;
            if (!cfg) return false;
            return cfg.elementId != null && cfg.elementId !== "";
        })
        .sort((a, b) => {
            const na = parseInt(a.id.replace(/\D/g, ""), 10) || 0;
            const nb = parseInt(b.id.replace(/\D/g, ""), 10) || 0;
            return na - nb;
        });

    const elements: IvrApiElement[] = configNodes.map((n) =>
        blockConfigToApiElement((n.data!.blockConfig ?? {}) as BlockCfg, flowId ?? null)
    );

    const schedules: IvrApiSchedule[] = [];
    for (const n of configNodes) {
        const cfg = (n.data?.blockConfig ?? {}) as BlockCfg;
        if ((cfg.startConfiguration as string) !== "Scheduler") continue;
        const rows =
            (cfg.schedulerSlotRows as IvrSchedulerSlotRow[] | undefined) ?? [];
        const elId = parseElementId(cfg);
        if (elId === 0 && cfg.elementId == null) continue;
        const schedKind = normalizeIvrSchedulerScheduleKind(
            cfg.schedulerScheduleKind
        );
        const rowsForApi = filterIvrSchedulerSlotRowsForScheduleApi(
            schedKind,
            rows
        );
        if (rowsForApi.length > 0) {
            schedules.push(...slotRowsToSchedules(elId, flowId, rowsForApi));
        }
    }

    const routes = collectRoutesFromEdges(nodes, edges);

    let entryElementId: number | null =
        configuration?.entryElementId != null
            ? Number(configuration.entryElementId)
            : null;
    if (Number.isNaN(entryElementId)) entryElementId = null;

    const entryEdge = edges.find(
        (e) =>
            e.source === IVR_BASE_NODE_ID &&
            e.target.startsWith(IVR_START_CONFIG_PREFIX)
    );
    if (entryEdge) {
        const tn = nodes.find((n) => n.id === entryEdge.target);
        const eid = tn?.data?.blockConfig?.elementId;
        const num = typeof eid === "number" ? eid : Number(eid);
        if (Number.isFinite(num) && !Number.isNaN(num)) {
            entryElementId = num;
        }
    } else if (
        elements.length > 0 &&
        (entryElementId == null || Number.isNaN(Number(entryElementId)))
    ) {
        const nonEndIds = elements
            .filter(
                (el) => (el.elementType ?? "").toUpperCase() !== "END"
            )
            .map((el) => el.elementId)
            .filter((id) => Number.isFinite(Number(id)))
            .map((id) => Number(id));
        entryElementId =
            nonEndIds.length > 0 ? Math.min(...nonEndIds) : elements[0].elementId;
    }

    const resolvedEntry =
        elements.length > 0
            ? resolveEffectiveIvrEntryElementId(entryElementId, elements)
            : entryElementId;

    return {
        elements,
        routes,
        schedules,
        configurationPatch: { entryElementId: resolvedEntry },
    };
}
